<script setup lang="ts">
import type { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SiteSectionCategoryId } from "@/data/common/ids";
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { computed } from "vue";

import { ActiveFilter } from "@/components/section/site-section-active-filters.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import Checkbox from "primevue/checkbox";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import SiteSectionsTreeApplianceCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/appliance-category-filter/site-sections-tree-appliance-category-filter.vue";
import SiteSectionsTreeSectionCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.vue";
import SiteSectionCategoryActiveFilters from "@/components/section/site-section-active-filters.vue";

type SiteSectionsTreeActiveFilter =
  | ActiveFilter<"appliance", EProgramApplianceCategory>
  | ActiveFilter<"section", SiteSectionCategoryId>;

const props = withDefaults(
  defineProps<{
    areAllVisibleSectionsSelected?: boolean;
    areVisibleSectionsPartiallySelected?: boolean;
    categoryOptions: readonly SiteSectionsTreeSectionCategoryFilterOption[];
    createCategory: (name: string) => Promise<boolean>;
    isCreatingCategory: boolean;
    showBulkEditionTrigger?: boolean;
    showSelectAll?: boolean;
    siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
    visibleSectionsCountLabel: string;
  }>(),
  {
    areAllVisibleSectionsSelected: false,
    areVisibleSectionsPartiallySelected: false,
    showBulkEditionTrigger: false,
    showSelectAll: false,
  },
);

const currentRouteName = computed(() => router.currentRoute.value.name);

const searchQuery = defineModel<string>("searchQuery", { required: true });
const selectedApplianceCategories = defineModel<EProgramApplianceCategory[]>("selectedApplianceCategories", {
  required: true,
});
const selectedSectionCategories = defineModel<SiteSectionCategoryId[]>("selectedSectionCategories", { required: true });

const emit = defineEmits<{
  (event: "enter-bulk-category-edition"): void;
  (event: "remove-appliance-category", category: EProgramApplianceCategory): void;
  (event: "remove-section-category", categoryId: SiteSectionCategoryId): void;
  (event: "toggle-visible-sections-selection", isSelected: boolean): void;
}>();

const { t, k } = useTypedIntl();

const activeCategoryFilters = computed<SiteSectionsTreeActiveFilter[]>(() => [
  ...selectedApplianceCategories.value.map((category) => ({
    type: "appliance" as const,
    id: category,
    label: t(k.global.applianceCategory[category]),
  })),
  ...selectedSectionCategories.value.flatMap((categoryId) => {
    const option = props.categoryOptions.find((categoryOption) => categoryOption.id === categoryId);

    return option
      ? [
          {
            type: "section" as const,
            id: categoryId,
            label: option.label,
          },
        ]
      : [];
  }),
]);

function removeActiveCategoryFilter(filter: SiteSectionsTreeActiveFilter) {
  if (filter.type === "appliance") {
    emit("remove-appliance-category", filter.id);
    return;
  }

  emit("remove-section-category", filter.id);
}

function handleSearchSectionQueryChanged() {
  MixpanelService.trackEvent(EMixpanelEvent.SECTION_MANAGEMENT_SEARCH_PERFORMED);
}

function handleSelectedSectionCategoriesChanged(newSelectedSectionCategoryIds: SiteSectionCategoryId[]) {
  if (newSelectedSectionCategoryIds.length) {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_APPLIED, {
      category_id: newSelectedSectionCategoryIds,
      page_context: currentRouteName.value?.toString(),
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
      page_context: currentRouteName.value?.toString(),
      method: "dropdown",
    });
  }
}

function handleSelectedApplianceCategoriesChanged(newSelectedApplianceCategoryIds: EProgramApplianceCategory[]) {
  if (newSelectedApplianceCategoryIds.length) {
    MixpanelService.trackEvent(EMixpanelEvent.APPLIANCE_TYPE_FILTER_APPLIED, {
      category_id: newSelectedApplianceCategoryIds,
      page_context: currentRouteName.value?.toString(),
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.APPLIANCE_TYPE_FILTER_CLEARED, {
      page_context: currentRouteName.value?.toString(),
      method: "dropdown",
    });
  }
}
</script>

<template>
  <div class="flex flex-col gap-4 @container">
    <div class="flex flex-1 items-center gap-3">
      <button
        v-if="props.showBulkEditionTrigger"
        type="button"
        class="site-section-select-pill"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.header.selectButton"
        @click="emit('enter-bulk-category-edition')"
      >
        <i class="pi pi-tag !text-[14px]" />
        <span>{{ t(k.tools.siteManagement.siteTreeStructure.select) }}</span>
      </button>

      <IconField
        iconPosition="left"
        class="flex flex-1 w-full"
      >
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          class="min-w-0 w-full !text-sm"
          :placeholder="t(k.tools.siteManagement.siteTreeStructure.searchSection)"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.header.searchTextInput"
          @value-change="handleSearchSectionQueryChanged"
        />
      </IconField>

      <SiteSectionsTreeApplianceCategoryFilter
        v-model:selected-categories="selectedApplianceCategories"
        :site-sections-tree="props.siteSectionsTree"
        @update:selected-categories="handleSelectedApplianceCategoriesChanged"
      />

      <SiteSectionsTreeSectionCategoryFilter
        v-model:selected-categories="selectedSectionCategories"
        :options="props.categoryOptions"
        :create-category="props.createCategory"
        :is-creating-category="props.isCreatingCategory"
        @update:selected-categories="handleSelectedSectionCategoriesChanged"
      />

      <span
        class="whitespace-nowrap overflow-hidden text-center text-sm text-gray-500 @[900px]:w-44 w-0 shrink-0 transition-all ease-in-out duration-200"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.header.visibleSectionsCounter"
      >
        {{ props.visibleSectionsCountLabel }}
      </span>
    </div>

    <label
      v-if="props.showSelectAll"
      class="flex w-fit items-center gap-2 text-sm font-normal text-gray-500"
    >
      <Checkbox
        binary
        :model-value="props.areAllVisibleSectionsSelected"
        :indeterminate="props.areVisibleSectionsPartiallySelected"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.bulkCategory.selectAllCheckbox"
        @update:model-value="emit('toggle-visible-sections-selection', $event)"
      />
      <span>{{ t(k.tools.siteManagement.siteTreeStructure.bulkCategory.selectAllVisible) }}</span>
    </label>

    <SiteSectionCategoryActiveFilters
      v-if="activeCategoryFilters.length"
      :active-category-filters="activeCategoryFilters"
      class="border-b pb-4"
      show-active-filters-text
      @remove-section-category="(filter) => removeActiveCategoryFilter(filter)"
    />
  </div>
</template>

<style lang="scss">
.site-section-select-pill {
  @apply flex min-h-10 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full border border-solid border-neutral-light bg-white px-3 py-2 text-sm font-semibold text-text-primary transition-colors duration-100;

  &:hover,
  &:focus-visible {
    @apply border-border-subtle bg-white text-text-secondary outline-none;
  }

  &:disabled {
    @apply cursor-default;
  }

  &:disabled:hover {
    @apply border-neutral-light bg-white text-text-primary;
  }
}
</style>
